import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  IAddDirectDeviceRequest,
  ICreateEnrollmentRequest,
  IEnrollCameraFaceRequest,
  IEnrollFromSightingRequest,
  ISetCameraPasswordRequest,
  TEnrollCameraFaceResponse,
  IGeneratePairingCodeRequest,
  IListDeviceEventsRequest,
  IListDeviceSightingsRequest,
  IListDevicesRequest,
  IListUnmappedEventsRequest,
  IMoveAgentDevicesRequest,
  IResolveUnmappedEventGroupRequest,
  IUnmappedEventGroupKey,
  IUpdateAgentRequest,
  IUpdateDeviceRequest,
  TEmployeeScopesResponse,
  TIgnoreUnmappedEventGroupResponse,
  TMoveAgentDevicesResponse,
  TAgentReleaseResponse,
  TDirectSetupResponse,
  TCameraSetupResponse,
  TListAgentsResponse,
  TListDeviceEventsResponse,
  TListDeviceSightingsResponse,
  TListUnmappedSightingGroupsResponse,
  TListDevicesResponse,
  TListEnrollmentsResponse,
  TListUnmappedEventGroupsResponse,
  TPairingCodeResponse,
  TResolveUnmappedEventResponse,
  TSingleAgentResponse,
  TSingleDeviceResponse,
  TSingleEnrollmentResponse,
  IListRecordingSegmentsRequest,
  IListCameraClipsRequest,
  TListCameraClipsResponse,
  TGetCameraClipResponse,
  IRecordingCompanySettings,
  IUpdateCameraRecordingRequest,
  TCameraRecordingSettingsResponse,
  TListRecordingSegmentsResponse,
  TRecordingCompanySettingsResponse,
  IAddCameraRequest,
  TAddCameraResponse,
  TListCamerasResponse,
  TCameraStorageResponse,
  TDeviceCapacityResponse,
  TLiveViewResponse,
} from "@/types/(company)/biometricDevices.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyBiometricDevicesApi = createApi({
  reducerPath: "companyBiometricDevicesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "DesktopAgents",
    "BiometricDevices",
    "BiometricDeviceEnrollments",
    "UnmappedEvents",
    "DeviceEvents",
    "FaceSightings",
    "CameraSetup",
    "CameraRecording",
    "RecordingSettings",
    "Cameras",
    "CameraStorage",
    "DeviceCapacity",
  ],
  endpoints: (builder) => ({
    generatePairingCode: builder.mutation<
      TPairingCodeResponse,
      IGeneratePairingCodeRequest
    >({
      query: (body) => ({
        url: "/company/biometric-devices/agents",
        method: "POST",
        body,
      }),
      invalidatesTags: ["DesktopAgents"],
    }),
    listAgents: builder.query<TListAgentsResponse, void>({
      query: () => ({
        url: "/company/biometric-devices/agents",
        method: "GET",
      }),
      providesTags: ["DesktopAgents"],
    }),
    getAgentRelease: builder.query<TAgentReleaseResponse, void>({
      query: () => ({
        url: "/company/biometric-devices/agents/release",
        method: "GET",
      }),
    }),
    updateAgent: builder.mutation<TSingleAgentResponse, IUpdateAgentRequest>({
      query: ({ id, data }) => ({
        url: `/company/biometric-devices/agents/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["DesktopAgents", "BiometricDevices"],
    }),
    moveAgentDevices: builder.mutation<
      TMoveAgentDevicesResponse,
      IMoveAgentDevicesRequest
    >({
      query: ({ id, targetAgentId }) => ({
        url: `/company/biometric-devices/agents/${id}/move-devices`,
        method: "POST",
        body: { targetAgentId },
      }),
      invalidatesTags: ["DesktopAgents", "BiometricDevices", "DeviceEvents"],
    }),
    regeneratePairingCode: builder.mutation<TPairingCodeResponse, number>({
      query: (agentId) => ({
        url: `/company/biometric-devices/agents/${agentId}/pairing-code`,
        method: "POST",
      }),
      invalidatesTags: ["DesktopAgents"],
    }),
    revokeAgent: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (agentId) => ({
        url: `/company/biometric-devices/agents/${agentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DesktopAgents"],
    }),

    listDevices: builder.query<
      TListDevicesResponse,
      IListDevicesRequest | void
    >({
      query: (params) => ({
        url: "/company/biometric-devices/devices",
        method: "GET",
        params: params ?? undefined,
      }),
      providesTags: ["BiometricDevices"],
    }),
    // The Attendance Devices plan's limit against use. Any change to the
    // device list can change it, so it refetches with the list.
    getDeviceCapacity: builder.query<TDeviceCapacityResponse, void>({
      query: () => ({
        url: "/company/biometric-devices/devices/capacity",
        method: "GET",
      }),
      providesTags: ["BiometricDevices", "DeviceCapacity"],
    }),
    addDirectDevice: builder.mutation<
      TSingleDeviceResponse,
      IAddDirectDeviceRequest
    >({
      query: (body) => ({
        url: "/company/biometric-devices/devices/direct",
        method: "POST",
        body,
      }),
      // The first one in a branch also creates its "Direct connection" agent.
      invalidatesTags: ["BiometricDevices", "DesktopAgents"],
    }),
    getDirectSetup: builder.query<TDirectSetupResponse, void>({
      query: () => ({
        url: "/company/biometric-devices/devices/direct/setup",
        method: "GET",
      }),
    }),
    // Settings-only on the API: the URL carries the camera's push token.
    getCameraSetup: builder.query<TCameraSetupResponse, number>({
      query: (id) => ({
        url: `/company/biometric-devices/devices/${id}/camera-setup`,
        method: "GET",
      }),
      providesTags: ["CameraSetup"],
    }),
    setCameraPassword: builder.mutation<
      { success: boolean; message: string },
      ISetCameraPasswordRequest
    >({
      query: ({ deviceId, password }) => ({
        url: `/company/biometric-devices/devices/${deviceId}/camera-password`,
        method: "PUT",
        body: { password },
      }),
      // Saving it also gives the camera its branch's faces.
      invalidatesTags: ["CameraSetup", "BiometricDeviceEnrollments"],
    }),
    updateDevice: builder.mutation<TSingleDeviceResponse, IUpdateDeviceRequest>(
      {
        query: ({ id, data }) => ({
          url: `/company/biometric-devices/devices/${id}`,
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: ["BiometricDevices"],
      },
    ),
    removeDevice: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/company/biometric-devices/devices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BiometricDevices"],
    }),

    getEmployeeScopes: builder.query<TEmployeeScopesResponse, number>({
      query: (userId) => ({
        url: `/company/biometric-devices/employees/${userId}/scopes`,
        method: "GET",
      }),
    }),

    listEnrollments: builder.query<TListEnrollmentsResponse, number>({
      query: (deviceId) => ({
        url: `/company/biometric-devices/devices/${deviceId}/enrollments`,
        method: "GET",
      }),
      providesTags: ["BiometricDeviceEnrollments"],
    }),
    createEnrollment: builder.mutation<
      TSingleEnrollmentResponse,
      ICreateEnrollmentRequest
    >({
      query: ({ deviceId, ...body }) => ({
        url: `/company/biometric-devices/devices/${deviceId}/enrollments`,
        method: "POST",
        body,
      }),
      // A face camera's unmapped recognitions of the ID go to the employee.
      invalidatesTags: ["BiometricDeviceEnrollments", "FaceSightings"],
    }),
    // Puts an employee's face on a face camera; the gateway sends it.
    enrollCameraFace: builder.mutation<
      TEnrollCameraFaceResponse,
      IEnrollCameraFaceRequest
    >({
      query: ({ deviceId, userId, photo, useProfilePhoto }) => {
        const body = new FormData();
        body.append("userId", String(userId));
        if (photo) body.append("photo", photo);
        else if (useProfilePhoto) body.append("useProfilePhoto", "true");
        return {
          url: `/company/biometric-devices/devices/${deviceId}/faces`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["BiometricDeviceEnrollments"],
    }),
    // The sighting's photo becomes the employee's face photo, sent to the
    // camera that took it and every camera in their branch.
    enrollFromSighting: builder.mutation<
      TEnrollCameraFaceResponse,
      IEnrollFromSightingRequest
    >({
      query: ({ sightingId, userId }) => ({
        url: `/company/biometric-devices/sightings/${sightingId}/enroll`,
        method: "POST",
        body: { userId },
      }),
      invalidatesTags: ["BiometricDeviceEnrollments"],
    }),
    removeEnrollment: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (enrollmentId) => ({
        url: `/company/biometric-devices/enrollments/${enrollmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BiometricDeviceEnrollments", "FaceSightings"],
    }),

    listUnmappedEventGroups: builder.query<
      TListUnmappedEventGroupsResponse,
      IListUnmappedEventsRequest | void
    >({
      query: (params) => ({
        url: "/company/biometric-devices/events/unmapped/groups",
        method: "GET",
        params: params ?? undefined,
      }),
      providesTags: ["UnmappedEvents"],
    }),
    resolveUnmappedEventGroup: builder.mutation<
      TResolveUnmappedEventResponse,
      IResolveUnmappedEventGroupRequest
    >({
      query: (body) => ({
        url: "/company/biometric-devices/events/unmapped/groups/resolve",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "UnmappedEvents",
        "BiometricDeviceEnrollments",
        "DeviceEvents",
      ],
    }),
    ignoreUnmappedEventGroup: builder.mutation<
      TIgnoreUnmappedEventGroupResponse,
      IUnmappedEventGroupKey
    >({
      query: (body) => ({
        url: "/company/biometric-devices/events/unmapped/groups/ignore",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UnmappedEvents", "DeviceEvents"],
    }),

    listDeviceEvents: builder.query<
      TListDeviceEventsResponse,
      IListDeviceEventsRequest
    >({
      query: ({ deviceId, ...params }) => ({
        url: `/company/biometric-devices/devices/${deviceId}/events`,
        method: "GET",
        params,
      }),
      providesTags: (_result, _error, arg) => [
        { type: "DeviceEvents", id: arg.deviceId },
      ],
    }),
    listDeviceSightings: builder.query<
      TListDeviceSightingsResponse,
      IListDeviceSightingsRequest
    >({
      query: ({ deviceId, ...params }) => ({
        url: `/company/biometric-devices/devices/${deviceId}/sightings`,
        method: "GET",
        params,
      }),
      providesTags: ["FaceSightings"],
    }),
    listUnmappedSightingGroups: builder.query<
      TListUnmappedSightingGroupsResponse,
      IListUnmappedEventsRequest | void
    >({
      query: (params) => ({
        url: "/company/biometric-devices/sightings/unmapped/groups",
        method: "GET",
        params: params ?? undefined,
      }),
      providesTags: ["FaceSightings"],
    }),
    retryFailedEvent: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/company/biometric-devices/events/${id}/retry`,
        method: "POST",
      }),
      invalidatesTags: ["DeviceEvents"],
    }),
    retryFailedEventsForDevice: builder.mutation<
      {
        success: boolean;
        message: string;
        data: { retried: number; succeeded: number };
      },
      number
    >({
      query: (deviceId) => ({
        url: `/company/biometric-devices/devices/${deviceId}/events/retry-failed`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, deviceId) => [
        { type: "DeviceEvents", id: deviceId },
      ],
    }),

    // ── Cameras module (gated on the Camera section) ──────────────────────
    listCameras: builder.query<TListCamerasResponse, void>({
      query: () => ({
        url: "/company/camera-recordings/cameras",
        method: "GET",
      }),
      providesTags: ["Cameras"],
    }),
    addCamera: builder.mutation<TAddCameraResponse, IAddCameraRequest>({
      query: (body) => ({
        url: "/company/camera-recordings/cameras",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cameras", "BiometricDevices"],
    }),
    // Same response as getCameraSetup, under the Camera section's Settings.
    getCameraPushSetup: builder.query<TCameraSetupResponse, number>({
      query: (deviceId) => ({
        url: `/company/camera-recordings/cameras/${deviceId}/push-setup`,
        method: "GET",
      }),
      providesTags: ["CameraSetup"],
    }),
    setCameraPasswordFromCameras: builder.mutation<
      { success: boolean; message: string },
      { deviceId: number; password: string }
    >({
      query: ({ deviceId, password }) => ({
        url: `/company/camera-recordings/cameras/${deviceId}/password`,
        method: "PUT",
        body: { password },
      }),
      // Saving it also gives the camera its branch's faces.
      invalidatesTags: ["CameraSetup", "BiometricDeviceEnrollments"],
    }),

    // Camera storage against the plan's limit (View-level).
    getCameraStorage: builder.query<TCameraStorageResponse, void>({
      query: () => ({
        url: "/company/camera-recordings/storage",
        method: "GET",
      }),
      providesTags: ["CameraStorage"],
    }),

    // ── Camera recordings ─────────────────────────────────────────────────
    getRecordingSettings: builder.query<
      TRecordingCompanySettingsResponse,
      void
    >({
      query: () => ({
        url: "/company/camera-recordings/settings",
        method: "GET",
      }),
      providesTags: ["RecordingSettings"],
    }),
    updateRecordingSettings: builder.mutation<
      TRecordingCompanySettingsResponse,
      IRecordingCompanySettings
    >({
      query: (body) => ({
        url: "/company/camera-recordings/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["RecordingSettings"],
    }),
    // Needs Settings: the response carries the camera's stream URL.
    getCameraRecording: builder.query<TCameraRecordingSettingsResponse, number>(
      {
        query: (deviceId) => ({
          url: `/company/camera-recordings/devices/${deviceId}/settings`,
          method: "GET",
        }),
        providesTags: (_r, _e, deviceId) => [
          { type: "CameraRecording", id: deviceId },
        ],
      },
    ),
    updateCameraRecording: builder.mutation<
      TCameraRecordingSettingsResponse,
      IUpdateCameraRecordingRequest
    >({
      query: ({ deviceId, mode, recordEvents }) => ({
        url: `/company/camera-recordings/devices/${deviceId}/settings`,
        method: "PUT",
        body: { mode, recordEvents },
      }),
      invalidatesTags: (_r, _e, { deviceId }) => [
        { type: "CameraRecording", id: deviceId },
        "Cameras",
      ],
    }),
    // Ready clips with the faces seen in each, newest first.
    listCameraClips: builder.query<
      TListCameraClipsResponse,
      IListCameraClipsRequest
    >({
      query: ({ deviceId, from, to }) => ({
        url: `/company/camera-recordings/devices/${deviceId}/clips`,
        method: "GET",
        params: { from, to },
      }),
    }),
    // A clip's presigned URL lasts an hour, so it is fetched on play.
    getCameraClip: builder.query<TGetCameraClipResponse, number>({
      query: (clipId) => ({
        url: `/company/camera-recordings/clips/${clipId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    listRecordingSegments: builder.query<
      TListRecordingSegmentsResponse,
      IListRecordingSegmentsRequest
    >({
      query: ({ deviceId, from, to }) => ({
        url: `/company/camera-recordings/devices/${deviceId}/segments`,
        method: "GET",
        params: { from, to },
      }),
    }),
    // Raw m3u8 text (not the JSON envelope), fetched here so the bearer
    // token and its refresh work as for every other call; the player gets
    // it as a blob URL. Its segment URLs are presigned S3 links.
    getRecordingPlaylist: builder.query<string, IListRecordingSegmentsRequest>({
      query: ({ deviceId, from, to }) => ({
        url: `/company/camera-recordings/cameras/${deviceId}/playlist.m3u8`,
        method: "GET",
        params: { from, to },
        responseHandler: "text",
      }),
      keepUnusedDataFor: 0,
    }),
    // A short-lived live-view link; never cached, each call is a new token.
    issueLiveView: builder.mutation<TLiveViewResponse, number>({
      query: (deviceId) => ({
        url: `/company/camera-recordings/cameras/${deviceId}/live`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGeneratePairingCodeMutation,
  useListAgentsQuery,
  useGetAgentReleaseQuery,
  useUpdateAgentMutation,
  useMoveAgentDevicesMutation,
  useRegeneratePairingCodeMutation,
  useRevokeAgentMutation,
  useListDevicesQuery,
  useAddDirectDeviceMutation,
  useGetDirectSetupQuery,
  useGetCameraSetupQuery,
  useUpdateDeviceMutation,
  useRemoveDeviceMutation,
  useGetEmployeeScopesQuery,
  useListEnrollmentsQuery,
  useCreateEnrollmentMutation,
  useEnrollCameraFaceMutation,
  useEnrollFromSightingMutation,
  useSetCameraPasswordMutation,
  useRemoveEnrollmentMutation,
  useListUnmappedEventGroupsQuery,
  useResolveUnmappedEventGroupMutation,
  useIgnoreUnmappedEventGroupMutation,
  useListDeviceEventsQuery,
  useListDeviceSightingsQuery,
  useListUnmappedSightingGroupsQuery,
  useRetryFailedEventMutation,
  useRetryFailedEventsForDeviceMutation,
  useGetRecordingSettingsQuery,
  useUpdateRecordingSettingsMutation,
  useGetCameraRecordingQuery,
  useUpdateCameraRecordingMutation,
  useListRecordingSegmentsQuery,
  useListCameraClipsQuery,
  useGetCameraClipQuery,
  useLazyGetRecordingPlaylistQuery,
  useIssueLiveViewMutation,
  useLazyListRecordingSegmentsQuery,
  useListCamerasQuery,
  useGetCameraStorageQuery,
  useGetDeviceCapacityQuery,
  useAddCameraMutation,
  useGetCameraPushSetupQuery,
  useSetCameraPasswordFromCamerasMutation,
} = companyBiometricDevicesApi;
